import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

export type SpecialtyGroup = 'validated' | 'pediatric' | 'adult';

export interface SpecialtyListItem {
  id: string;
  displayName: string;
  group: SpecialtyGroup;
  hasOverride: boolean;
}

interface Props {
  items: SpecialtyListItem[];
  selected: string;
  onSelect: (id: string) => void;
  dirtyId: string | null;
}

const GROUP_LABEL: Record<SpecialtyGroup, string> = {
  validated: 'Validated',
  pediatric: 'Pediatric',
  adult: 'Adult',
};

const GROUP_ORDER: SpecialtyGroup[] = ['validated', 'pediatric', 'adult'];

export function SpecialtyList({ items, selected, onSelect, dirtyId }: Props) {
  const [query, setQuery] = useState('');
  const lower = query.trim().toLowerCase();
  const filtered = lower
    ? items.filter(i => i.displayName.toLowerCase().includes(lower))
    : items;

  const grouped: Record<SpecialtyGroup, SpecialtyListItem[]> = {
    validated: [], pediatric: [], adult: [],
  };
  filtered.forEach(i => grouped[i.group].push(i));

  return (
    <div className="flex flex-col h-full">
      <div className="p-2">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search specialties…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="pl-7 h-8 text-sm"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {GROUP_ORDER.map(group => grouped[group].length > 0 && (
          <div key={group} className="mb-3">
            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 px-2 py-1">
              {GROUP_LABEL[group]} <span className="font-normal">({grouped[group].length})</span>
            </div>
            {grouped[group].map(item => (
              <button
                key={item.id}
                type="button"
                data-testid="specialty-row"
                data-selected={selected === item.id}
                onClick={() => onSelect(item.id)}
                className={cn(
                  'w-full text-left text-sm rounded px-2 py-1.5 flex items-center gap-2 transition-colors',
                  selected === item.id
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'hover:bg-gray-100 text-gray-800',
                )}
              >
                <span className="flex-1 truncate">{item.displayName}</span>
                {item.hasOverride && (
                  <Badge
                    variant="outline"
                    className="text-[9px] px-1 py-0 h-4 bg-amber-50 text-amber-800 border-amber-200"
                  >
                    edited
                  </Badge>
                )}
                {dirtyId === item.id && (
                  <span data-testid="dirty-dot" className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                )}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
